import React, { useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import {
  AcceptanceChangeContextType,
  acceptanceChangeRequestApi,
} from '../../services/acceptanceChangeRequestApi';
import { AcceptanceManifest } from './types';
import { getActiveAcceptanceRun } from './AcceptanceRunControls';

interface Props {
  manifest: AcceptanceManifest;
  contextType: AcceptanceChangeContextType;
  fieldId?: string;
  controlId?: string;
  documentKey?: string;
  inputSummary?: unknown;
  evidenceContext?: unknown;
  label?: string;
  size?: 'small' | 'middle' | 'large';
}

const currentRoleContext = () => {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : {};
    return JSON.stringify({ userId: user.userId, roles: user.roles || [] });
  } catch {
    return 'unavailable';
  }
};

export const AcceptanceChangeRequestButton: React.FC<Props> = ({
  manifest, contextType, fieldId, controlId, documentKey,
  inputSummary, evidenceContext, label = '提出 Contract 變更', size = 'small',
}) => {
  const [open, setOpen] = useState(false);
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const expected = expectedBehavior.trim();
    if (!expected) return;
    setSubmitting(true);
    try {
      const request = await acceptanceChangeRequestApi.create({
        formId: manifest.formId,
        chainId: manifest.chainId,
        contextType,
        fieldId,
        controlId,
        contractHash: manifest.contractHash,
        documentKey,
        acceptanceRunId: getActiveAcceptanceRun(manifest.formId)?.runId,
        environment: window.location.host,
        roleContext: currentRoleContext(),
        inputSummary: inputSummary || {},
        evidenceContext: evidenceContext || {},
        expectedBehavior: expected,
        attachmentRefs: [],
      });
      message.success(`Contract 變更提案 #${request.requestId} 已送出；不會直接修改 Contract 或業務資料。`);
      setExpectedBehavior('');
      setOpen(false);
    } catch {
      message.error('Contract 變更提案送出失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return <>
    <Button size={size} icon={<FileAddOutlined />} onClick={() => setOpen(true)}>{label}</Button>
    <Modal
      title="提出 Contract Change Request"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={submit}
      okButtonProps={{ disabled: !expectedBehavior.trim(), loading: submitting }}
      okText="送出提案"
    >
      <p>這只會建立獨立提案，不會直接修改 Contract、Git 或 ERP 業務資料。</p>
      <Input.TextArea
        rows={5}
        value={expectedBehavior}
        onChange={event => setExpectedBehavior(event.target.value)}
        placeholder="請描述你認為正確的業務行為、限制、公式或流程"
      />
    </Modal>
  </>;
};
